// src/components/common/NavBar.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import {
  FaUserCircle, FaTachometerAlt, FaUserEdit,
  FaTag, FaEnvelope, FaSignOutAlt, FaBars,
  FaTimes, FaChevronDown, FaUserShield,
} from 'react-icons/fa';
import { useAuth } from '../../hooks/useAuth';

/* ─── helpers ─────────────────────────────────────────────────────────────── */
const initials = (name = '') =>
  name.split(/[\s._-]+/).slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('') || '?';

/* ─── sub-components ──────────────────────────────────────────────────────── */
const DesktopLink = ({ to, children }) => (
  <NavLink to={to}
    className={({ isActive }) =>
      ['relative text-sm font-medium pb-px transition-colors duration-150',
       'after:absolute after:inset-x-0 after:bottom-0 after:h-[2px] after:rounded-full after:bg-brand-red after:transition-all after:duration-200',
       isActive ? 'text-brand-red after:opacity-100' : 'text-gray-700 hover:text-brand-red after:opacity-0 hover:after:opacity-100',
      ].join(' ')}
  >{children}</NavLink>
);

const DrawerLink = ({ to, children, onClick }) => (
  <NavLink to={to} onClick={onClick}
    className={({ isActive }) =>
      ['flex items-center px-3 py-2.5 rounded-lg text-sm font-medium border-l-[3px] transition-all duration-150',
       isActive ? 'border-brand-red bg-brand-red/8 text-brand-red font-semibold' : 'border-transparent text-gray-700 hover:bg-gray-50 hover:text-gray-900',
      ].join(' ')}
  >{children}</NavLink>
);

const DropItem = ({ to, onClick, Icon, children, danger }) => {
  const cls = `flex items-center gap-3 w-full px-3 py-2 text-sm rounded-lg transition-colors duration-100 ${
    danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50'
  }`;
  return to
    ? <Link to={to} onClick={onClick} className={cls}><Icon className="w-3.5 h-3.5 opacity-60 shrink-0"/>{children}</Link>
    : <button onClick={onClick} className={`${cls} text-left`}><Icon className="w-3.5 h-3.5 opacity-60 shrink-0"/>{children}</button>;
};

/* ─── main ────────────────────────────────────────────────────────────────── */
const NavBar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const roles = user?.roles ?? [];
  const isAdmin     = roles.includes('ADMIN');
  const isSiteOwner = roles.includes('SITE_OWNER');
  const isUser      = roles.includes('USER');
  const isBoth      = isAdmin && isSiteOwner;

  const [scrolled,      setScrolled]      = useState(false);
  const [userOpen,      setUserOpen]      = useState(false);
  const [liveOpen,      setLiveOpen]      = useState(false);
  const [mobileOpen,    setMobileOpen]    = useState(false);

  const userRef = useRef(null);
  const liveRef = useRef(null);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 6);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    const fn = e => {
      if (!userRef.current?.contains(e.target)) setUserOpen(false);
      if (!liveRef.current?.contains(e.target)) setLiveOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const close = () => setMobileOpen(false);

  const handleLogout = async e => {
    e.preventDefault();
    setUserOpen(false); setMobileOpen(false);
    await logout();
    navigate('/login');
  };

  return (
    <>
      {/* ── Bar ──────────────────────────────────────────────────────────── */}
      <header className={['fixed inset-x-0 top-0 z-50 bg-white transition-all duration-300',
        scrolled ? 'shadow-[0_2px_24px_rgba(0,0,0,0.09)]' : 'border-b border-gray-100',
      ].join(' ')}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-6">

          {/* Logo */}
          <Link to="/" className="shrink-0 flex items-center" aria-label="Home">
            <img src="/1.svg" alt="Akture" className="h-9 w-auto object-contain select-none" draggable={false}/>
          </Link>

          {/* Desktop links */}
          <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
            <DesktopLink to="/">Home</DesktopLink>
            <DesktopLink to="/explore">Purchase</DesktopLink>
            <DesktopLink to="/all-livestream-events">All Livestreams</DesktopLink>
            <DesktopLink to="/pricing">Pricing</DesktopLink>
            {(isUser || isAdmin) && <DesktopLink to="/dashboard">My Videos</DesktopLink>}
            {isAdmin           && <DesktopLink to="/admin/dashboard">Admin</DesktopLink>}
            {isSiteOwner       && <DesktopLink to="/court-management">Courts</DesktopLink>}

            {/* Livestream dropdown */}
            {(isBoth || isSiteOwner) && (
              <div className="relative" ref={liveRef}>
                <button onClick={() => setLiveOpen(p => !p)}
                  className={`flex items-center gap-1 text-sm font-medium transition-colors duration-150 ${liveOpen ? 'text-brand-red' : 'text-gray-700 hover:text-brand-red'}`}>
                  Livestreams
                  <FaChevronDown className={`text-[10px] transition-transform duration-200 ${liveOpen ? 'rotate-180' : ''}`}/>
                </button>
                {liveOpen && (
                  <div className="absolute top-full left-0 mt-2.5 w-48 bg-white rounded-xl shadow-large border border-gray-100 py-1.5 z-50"
                    style={{animation:'fadeDown .14s ease-out'}}>
                    <Link to="/all-livestream-events" onClick={() => setLiveOpen(false)}
                      className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-brand-red transition-colors rounded-lg mx-1">
                      Manage Livestreams
                    </Link>
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* Right */}
          <div className="flex items-center gap-3 shrink-0">
            {user ? (
              /* User chip */
              <div className="relative" ref={userRef}>
                <button onClick={() => setUserOpen(p => !p)}
                  className="flex items-center gap-2.5 pl-1.5 pr-3 py-1 rounded-full border border-gray-200 hover:border-gray-300 bg-white transition-all duration-150"
                  aria-expanded={userOpen}>
                  <span className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center text-white text-[11px] font-bold shrink-0 select-none">
                    {initials(user.username)}
                  </span>
                  <span className="hidden sm:block text-xs font-medium text-gray-800 max-w-[110px] truncate">{user.username}</span>
                  <FaChevronDown className={`text-[10px] text-gray-400 transition-transform duration-200 ${userOpen ? 'rotate-180' : ''}`}/>
                </button>

                {userOpen && (
                  <div className="absolute right-0 top-full mt-2.5 w-56 bg-white rounded-2xl shadow-large border border-gray-100 overflow-hidden z-50"
                    style={{animation:'fadeDown .14s ease-out'}}>
                    {/* Header */}
                    <div className="px-4 py-3.5 border-b border-gray-100">
                      <p className="text-[10px] text-gray-400 mb-0.5">Signed in as</p>
                      <p className="text-sm font-bold text-gray-900 truncate">{user.username}</p>
                      {isAdmin && (
                        <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-brand-red/10 text-brand-red text-[10px] font-semibold rounded-full">
                          <FaUserShield className="text-[9px]"/> Admin
                        </span>
                      )}
                    </div>
                    {/* Items */}
                    <div className="p-1.5 space-y-px">
                      {isAdmin      && <DropItem to="/admin/dashboard"       onClick={() => setUserOpen(false)} Icon={FaTachometerAlt}>Admin Dashboard</DropItem>}
                      {(isUser||isAdmin) && <DropItem to="/dashboard"        onClick={() => setUserOpen(false)} Icon={FaTachometerAlt}>My Videos</DropItem>}
                      <DropItem to="/profile"          onClick={() => setUserOpen(false)} Icon={FaUserEdit}>Profile</DropItem>
                      <DropItem to="/account/subscription" onClick={() => setUserOpen(false)} Icon={FaTag}>My Subscription</DropItem>
                      <DropItem to="/pricing"          onClick={() => setUserOpen(false)} Icon={FaTag}>Pricing</DropItem>
                      {isAdmin && <DropItem to="/search-users"               onClick={() => setUserOpen(false)} Icon={FaUserCircle}>Search Users</DropItem>}
                      <DropItem to="/contact"          onClick={() => setUserOpen(false)} Icon={FaEnvelope}>Contact Support</DropItem>
                    </div>
                    {/* Logout */}
                    <div className="p-1.5 border-t border-gray-100">
                      <DropItem onClick={handleLogout} Icon={FaSignOutAlt} danger>Logout</DropItem>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" className="hidden sm:inline-block text-sm font-medium text-gray-700 hover:text-brand-red transition-colors px-1">Login</Link>
                <Link to="/register" className="inline-flex items-center px-4 py-2 bg-brand-red text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors shadow-sm">
                  Get Started
                </Link>
              </div>
            )}

            {/* Hamburger */}
            <button onClick={() => setMobileOpen(p => !p)}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu">
              {mobileOpen ? <FaTimes className="w-4 h-4"/> : <FaBars className="w-4 h-4"/>}
            </button>
          </div>
        </div>
      </header>

      {/* ── Overlay ──────────────────────────────────────────────────────── */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden" onClick={close}/>
      )}

      {/* ── Drawer ───────────────────────────────────────────────────────── */}
      <aside className={['fixed top-0 right-0 bottom-0 z-50 w-72 bg-white shadow-2xl lg:hidden flex flex-col transition-transform duration-300',
        mobileOpen ? 'translate-x-0' : 'translate-x-full'].join(' ')}>

        {/* Drawer header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100 shrink-0">
          <img src="/1.svg" alt="Akture" className="h-8 w-auto object-contain" draggable={false}/>
          <button onClick={close} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100" aria-label="Close">
            <FaTimes className="w-3.5 h-3.5"/>
          </button>
        </div>

        {/* User pill */}
        {user && (
          <div className="mx-3 mt-3 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl flex items-center gap-3 shrink-0">
            <span className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-red to-brand-orange flex items-center justify-center text-white text-xs font-bold shrink-0">
              {initials(user.username)}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{user.username}</p>
              {isAdmin && <span className="text-[10px] text-brand-red font-semibold">Admin</span>}
            </div>
          </div>
        )}

        {/* Links */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
          <p className="text-[9px] font-black text-gray-400 uppercase tracking-[0.18em] px-3 py-1.5">Navigation</p>
          <DrawerLink to="/"                       onClick={close}>Home</DrawerLink>
          <DrawerLink to="/explore"                onClick={close}>Purchase</DrawerLink>
          <DrawerLink to="/all-livestream-events"  onClick={close}>All Livestreams</DrawerLink>
          <DrawerLink to="/pricing"                onClick={close}>Pricing</DrawerLink>
          {(isUser||isAdmin) && <DrawerLink to="/dashboard"     onClick={close}>My Videos</DrawerLink>}
          {isAdmin           && <DrawerLink to="/admin/dashboard" onClick={close}>Admin Dashboard</DrawerLink>}
          {isSiteOwner       && <DrawerLink to="/court-management" onClick={close}>Court Management</DrawerLink>}
          {(isBoth||isSiteOwner) && <DrawerLink to="/all-livestream-events" onClick={close}>Manage Livestreams</DrawerLink>}
          {user && (
            <>
              <p className="text-[9px] font-black text-gray-400 uppercase tracking-[0.18em] px-3 pt-4 pb-1.5">Account</p>
              <DrawerLink to="/profile"             onClick={close}>Profile</DrawerLink>
              <DrawerLink to="/account/subscription" onClick={close}>My Subscription</DrawerLink>
              <DrawerLink to="/contact"             onClick={close}>Contact Support</DrawerLink>
              {isAdmin && <DrawerLink to="/search-users" onClick={close}>Search Users</DrawerLink>}
            </>
          )}
        </nav>

        {/* Drawer footer */}
        <div className="px-3 py-3 border-t border-gray-100 shrink-0 space-y-2">
          {user ? (
            <button onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 text-red-600 rounded-lg text-sm font-semibold hover:bg-red-100 transition-colors">
              <FaSignOutAlt className="w-3.5 h-3.5"/> Logout
            </button>
          ) : (
            <div className="flex flex-col gap-2">
              <Link to="/login" onClick={close}
                className="w-full flex items-center justify-center px-4 py-2.5 border border-gray-200 text-gray-800 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors">Login</Link>
              <Link to="/register" onClick={close}
                className="w-full flex items-center justify-center px-4 py-2.5 bg-brand-red text-white rounded-lg text-sm font-bold hover:bg-red-700 transition-colors">Get Started</Link>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default NavBar;
